//users/auth.Auth.js
import React, { useContext, useState } from "react";

import Input from "../../../shared/components/FormElements/Input";
import Button from "../../../shared/components/FormElements/Button";
import {
  VALIDATOR_EMAIL,
  VALIDATOR_MINLENGTH,
} from "../../../shared/util/validators";
import { useForm } from "../../../shared/hooks/form-hook";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { AuthContext } from "../../../shared/context/auth-context";
import "./Auth.scss";
import logo from "./ht-logo.png";

import toast from "react-hot-toast";
import { useLoading } from "../../../shared/context/LoadingContext";

const Auth = () => {
  const { startLoading, stopLoading } = useLoading();

  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();

  const [formState, inputHandler] = useForm(
    {
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
    },
    false,
  );

  // Clínicas com mais do que uma unidade ativa: o backend valida as
  // credenciais, devolve a lista e NÃO emite token. Guardamos a lista aqui e
  // voltamos a submeter o login já com a unidade escolhida.
  const [estabelecimentos, setEstabelecimentos] = useState(null);

  const autenticar = async (estabelecimentoId) => {
    startLoading();
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/users/login`,
        "POST",
        JSON.stringify({
          email: formState.inputs.email.value,
          password: formState.inputs.password.value,
          ...(estabelecimentoId && { estabelecimentoId }),
        }),
        {
          "Content-Type": "application/json",
        },
      );

      if (responseData.requerEstabelecimento) {
        setEstabelecimentos(responseData.estabelecimentos);
        return;
      }

      let usuariosAssociados = null;
      let secretariasDoMedico = null;
      let assistentesDoMedico = null;

      if (
        (responseData.role.role === "Assistente" ||
          responseData.role.role === "Secretária(o)") &&
        responseData.medicos &&
        responseData.medicos?.length > 0
      ) {
        usuariosAssociados = responseData.medicos.map((m) => {
          return {
            id: m._id,
            nome: m.name,
            nomeAbreviado: m.name
              ? m.name.split(/\s+/)[0] +
                " " +
                m.name.split(/\s+/)[m.name.split(/\s+/).length - 1]
              : "",
            cor: m.cor,
            duracaoConsulta: m.duracaoConsulta,
            horarioAtendimento: m.horarioAtendimento,
          };
        });
      }

      if (
        (responseData.role.role === "Dentista" ||
          responseData.role.role === "Médico/Administrador") &&
        responseData.secretarias &&
        responseData.secretarias?.length > 0
      ) {
        usuariosAssociados = responseData.secretarias.map((s) => {
          return { id: s._id, nome: s.name };
        });
      }

      if (responseData.role.role === "Assistente") {
        secretariasDoMedico = responseData.medicos.map((m) => {
          return {
            medicoId: m._id,
            secretarias: m.secretarias.filter(
              (sec) => sec !== responseData.userId,
            ),
          };
        });
      }

      if (responseData.role.role === "Secretária(o)") {
        assistentesDoMedico = responseData.medicos.map((m) => {
          return {
            medicoId: m._id,
            assistentes: m.assistentes.filter(
              (as) => as !== responseData.userId,
            ),
          };
        });
      }

      const tempPermissoes = new Set();

      responseData.perfis.forEach((perfil) => {
        perfil.permissoes.forEach((per) => {
          if (per.check) {
            tempPermissoes.add(per.permissao.shortCode);
          }
        });
      });

      const permissoes = Array.from(tempPermissoes);
      if (
        responseData.role.role === "Dentista" &&
        usuariosAssociados.length === 0
      ) {
        toast.error(
          "O administrador deve associar uma secretária a esse médico para que esse possa fazer login!",
        );
      } else {
        auth.login(
          responseData.userId,
          responseData.token,
          responseData.image,
          responseData.role.role,
          responseData.listaRoles,
          responseData.clinica,
          usuariosAssociados,
          secretariasDoMedico,
          assistentesDoMedico,
          permissoes,
          responseData.nome,
          responseData.genero,
          responseData.playNotificationSound,
          undefined,
          responseData.estabelecimento,
        );
      }
      // auth.image = responseData.image;
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
  };

  const authSubmitHandler = (event) => {
    event.preventDefault();
    autenticar();
  };

  return (
    <React.Fragment>
      <div className="auth-main-container">
        <div className="circle circle-1"></div>
        <div className="circle circle-2"></div>
        <div className="circle circle-3"></div>
        <div className="circle circle-4"></div>
        <div className="circle circle-5"></div>
        {/* A escolha da unidade toma o ecrã todo, em vez de caber no painel do
            login. É uma decisão com consequências — determina a morada dos
            documentos, o LED das faturas, a agenda e a caixa do dia — e merece
            uma pergunta inteira, não um passo apertado ao lado do formulário.
            Entrar no balcão errado nota-se tarde, e desfaz-se mal. */}
        {estabelecimentos ? (
          <div className="escolha-unidade-ecra">
            <img src={logo} alt="" className="escolha-unidade-ecra__logo" />
            <h1 className="escolha-unidade-ecra__titulo">
              Onde vai trabalhar hoje?
            </h1>
            <p className="escolha-unidade-ecra__ajuda">
              Escolha a unidade. É ela que define a morada dos documentos, a
              agenda e as caixas que vai ver.
            </p>

            <div className="escolha-unidade-ecra__opcoes">
              {estabelecimentos.map((e) => (
                <button
                  key={e._id}
                  type="button"
                  className="unidade-cartao"
                  onClick={() => autenticar(e._id)}
                >
                  <span className="unidade-cartao__codigo">{e.codigo}</span>
                  <span className="unidade-cartao__nome">{e.nome}</span>
                  {(e.cidade || e.endereco) && (
                    <span className="unidade-cartao__local">
                      {e.cidade || e.endereco}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="escolha-unidade-ecra__voltar"
              onClick={() => setEstabelecimentos(null)}
            >
              ← Voltar ao início de sessão
            </button>
          </div>
        ) : (
          <div className="auth-container">
            <div
              className={`auth-container-containers auth-container-1 ${
                process.env.REACT_APP_AMBIENTE === "teste"
                  ? " auth-container-1__teste"
                  : ""
              }`}
            ></div>

            <div className="auth-container-containers auth-container-2">
              <img src={logo} alt="" className="ht-logo" />
              <h2 style={{ textAlign: "center" }}>
                Seja Bem-Vindo{" "}
                {process.env.REACT_APP_AMBIENTE === "teste" ? (
                  <p>AMBIENTE TESTE</p>
                ) : (
                  ""
                )}
              </h2>
              <hr />
              <form onSubmit={authSubmitHandler} className="form-auth">
                <Input
                  element="input"
                  id="email"
                  type="email"
                  placeholder="Email"
                  validators={[VALIDATOR_EMAIL()]}
                  errorText="Por favor introduza um email válido."
                  onInput={inputHandler}
                />
                <Input
                  element="input"
                  id="password"
                  type="password"
                  placeholder="Palavra-passe"
                  validators={[VALIDATOR_MINLENGTH(6)]}
                  errorText="Por favor introduza uma palavra-passe válida, mínimo 6 caracteres."
                  onInput={inputHandler}
                  autoComplete="on"
                />
                <Button type="submit" disabled={!formState.isValid}>
                  Login
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Auth;
